import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { DateModel } from '../../_kernel/DateModel'
import { Money } from '../../_kernel/Money'
import type { Currencies } from '../../_kernel/Currency'
import { Currency } from '../../_kernel/Currency'
import type { Market } from '../../shared/domain/Market'

export const CancellationPolicyValidation = z.object({
  fromDate: z.instanceof(DateModel, { message: '[CancellationPolicy] fromDate is required' }),
  price: z.instanceof(Money, { message: '[CancellationPolicy] price is required' }),
})

export class CancellationPolicy extends Model {
  static create({ fromDate, price }: z.infer<typeof CancellationPolicyValidation>) {
    CancellationPolicyValidation.parse({ fromDate, price })
    return new CancellationPolicy(fromDate, price)
  }

  static fromJSON(json: ReturnType<CancellationPolicy['toJSON']>) {
    return CancellationPolicy.create({
      fromDate: DateModel.create({ value: json.fromDate }),
      price: Money.create({
        amount: json.price.amount,
        currency: Currency.create({ value: json.price.currency }),
      }),
    })
  }

  static fromLiterals({
    fromDate,
    amount,
    currency,
  }: {
    fromDate: string
    amount: number
    currency: Currencies
  }) {
    return CancellationPolicy.create({
      fromDate: DateModel.create({ value: fromDate }),
      price: Money.create({ amount, currency: Currency.create({ value: currency }) }),
    })
  }

  constructor(
    private readonly _fromDate: DateModel,
    private readonly _price: Money
  ) {
    super()
  }

  get fromDate() { return this._fromDate.value } // prettier-ignore

  amount() {
    return this._price.amount
  }
  currency() {
    return this._price.getCurrency()
  }

  toJSON() {
    return { fromDate: this.fromDate, price: this._price.toJSON() }
  }

  getFromDateFormatted(locale: string) {
    return this._fromDate.getFormattedShort(locale)
  }

  getPriceFormatted(locale: string, market: Market) {
    return this._price.toFormattedString(locale, market)
  }
}
